import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";
import { Quote, ArrowLeft, ArrowRight, Star } from "lucide-react";
import { Reveal } from "../primitives/Reveal";
import { AccentTitle } from "../primitives/AccentTitle";

const EASE = [0.16, 1, 0.3, 1];

// Designer-supplied senior business portraits (from design_guidelines.json)
const PORTRAITS = [
    "https://images.unsplash.com/photo-1584940120505-117038d90b05?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTJ8MHwxfHNlYXJjaHwxfHxzZW5pb3IlMjBidXNpbmVzcyUyMHBvcnRyYWl0JTIwcHJvZmVzc2lvbmFsfGVufDB8fHx8MTc3NzQ0NjU1NHww&ixlib=rb-4.1.0&q=85",
    "https://images.unsplash.com/photo-1584940121730-93ffb8aa88b0?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTJ8MHwxfHNlYXJjaHwzfHxzZW5pb3IlMjBidXNpbmVzcyUyMHBvcnRyYWl0JTIwcHJvZmVzc2lvbmFsfGVufDB8fHx8MTc3NzQ0NjU1NHww&ixlib=rb-4.1.0&q=85",
    "https://images.pexels.com/photos/8068772/pexels-photo-8068772.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
];

export const Testimonials = () => {
    const { t } = useTranslation();
    const items = t("home.testimonials.items", { returnObjects: true }) || [];
    const [idx, setIdx] = useState(0);
    const item = items[idx];

    const next = () => setIdx((idx + 1) % items.length);
    const prev = () => setIdx((idx - 1 + items.length) % items.length);

    if (!item) return null;

    return (
        <section className="bg-mudbrick-200 py-24 lg:py-32" data-testid="testimonials-section">
            <div className="mx-auto max-w-[1320px] px-5 lg:px-8">
                <Reveal className="max-w-3xl">
                    <p className="eyebrow">{t("home.testimonials.kicker")}</p>
                    <AccentTitle
                        component="h2"
                        className="section-headline mt-5"
                        title={t("home.testimonials.title")}
                        accent={t("home.testimonials.titleAccent")}
                        accentColor="#8A1E12"
                    />
                </Reveal>

                <div className="mt-14 grid grid-cols-1 lg:grid-cols-12 gap-8 items-stretch">
                    {/* LEFT: portrait — large architectural block */}
                    <div className="lg:col-span-5">
                        <div className="relative h-full min-h-[420px] overflow-hidden rounded-card border-2 border-ink-800 bg-ink-900">
                            <AnimatePresence mode="wait">
                                <motion.img
                                    key={idx}
                                    src={PORTRAITS[idx % PORTRAITS.length]}
                                    alt={item.name}
                                    initial={{ opacity: 0, scale: 1.05 }}
                                    animate={{ opacity: 1, scale: 1 }}
                                    exit={{ opacity: 0 }}
                                    transition={{ duration: 0.5, ease: EASE }}
                                    className="absolute inset-0 h-full w-full object-cover"
                                    data-testid={`testimonial-portrait-${idx}`}
                                />
                            </AnimatePresence>
                            {/* Madder corner block */}
                            <div className="absolute bottom-0 start-0 bg-madder-500 px-5 py-3">
                                <div className="flex items-center gap-1">
                                    {[1, 2, 3, 4, 5].map((s) => (
                                        <Star key={s} className="h-4 w-4 fill-souq-400 text-souq-400" strokeWidth={0} />
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* RIGHT: quote */}
                    <div className="lg:col-span-7 flex flex-col justify-between">
                        <div>
                            <Quote className="h-12 w-12 text-madder-500" strokeWidth={2.4} />
                            <AnimatePresence mode="wait">
                                <motion.div
                                    key={idx}
                                    initial={{ opacity: 0, y: 12 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0, y: -8 }}
                                    transition={{ duration: 0.45, ease: EASE }}
                                >
                                    <blockquote className="mt-6 font-display text-[28px] sm:text-[32px] lg:text-[40px] font-bold leading-[1.2] tracking-tight text-ink-800">
                                        “{item.quote}”
                                    </blockquote>
                                    <figcaption className="mt-8">
                                        <div className="text-[18px] font-bold text-ink-800" data-testid={`testimonial-name-${idx}`}>{item.name}</div>
                                        <div className="mt-1 text-[16px] font-semibold text-ink-600">{item.role}</div>
                                    </figcaption>
                                </motion.div>
                            </AnimatePresence>
                        </div>

                        {/* Controls */}
                        <div className="mt-10 flex items-center gap-3">
                            <button
                                type="button"
                                onClick={prev}
                                aria-label="Previous"
                                data-testid="testimonial-prev"
                                className="inline-flex h-14 w-14 items-center justify-center rounded-full border-2 border-ink-800 bg-white text-ink-800 transition-all duration-200 hover:bg-ink-800 hover:text-white"
                            >
                                <ArrowLeft className="h-5 w-5 rtl-mirror" />
                            </button>
                            <button
                                type="button"
                                onClick={next}
                                aria-label="Next"
                                data-testid="testimonial-next"
                                className="inline-flex h-14 w-14 items-center justify-center rounded-full border-2 border-ink-800 bg-ink-800 text-white transition-all duration-200 hover:bg-madder-500 hover:border-madder-500"
                            >
                                <ArrowRight className="h-5 w-5 rtl-mirror" />
                            </button>
                            <div className="ms-3 flex items-center gap-2">
                                {items.map((_, i) => (
                                    <button
                                        key={i}
                                        type="button"
                                        onClick={() => setIdx(i)}
                                        aria-label={`Go to ${i + 1}`}
                                        data-testid={`testimonial-dot-${i}`}
                                        className={`h-2.5 rounded-full transition-all duration-200 ${i === idx ? "w-10 bg-madder-500" : "w-2.5 bg-ink-300 hover:bg-ink-500"}`}
                                    />
                                ))}
                            </div>
                            <span className="ms-auto font-mono text-[14px] font-bold text-ink-600 tabular-nums num-ltr">
                                {String(idx + 1).padStart(2, "0")} / {String(items.length).padStart(2, "0")}
                            </span>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};
